/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useCallback, useEffect, useState } from 'react';
import mbpLogger from '@module-logger/mbp';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { useExitIntent, useScrollDetection } from './utils';
import OfferInboxContainer from '../../../components/GraphqlComponents/GraphqlHomePage/Partials/PersonalizedOfferInbox/OfferInboxContainer';
import { setOfferInboxData, setOfferInboxDrawerOpen } from '../../../../state/ducks/App/App-Actions';
import { emitCustomTrackingEvent } from '../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { getOfferInboxData, getPageType } from '../../../../state/ducks/App/App-Selectors';
import OfferInboxPopup from '../../../components/GraphqlComponents/GraphqlHomePage/Partials/PersonalizedOfferInbox/OfferInboxPopup/OfferInboxPopup';
import useExperienceInboxQuery from '../useExperienceInboxQuery';
import { OFFER_INBOX_ENTRANCE_ITEM, OFFER_INBOX_EXIT_INTENT_ITEM } from '../../../components/GraphqlComponents/GraphqlHomePage/Partials/PersonalizedOfferInbox/offerInboxTracking';
import { getBrandName } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import useBannerExclusions from '../../hooks/useBannerExclusions';

const INSTANCE_ID = 'exit-offer-inbox';
const EXIT_SESSION_KEY = 'exit_intent_triggered';
const ENTRANCE_SESSION_KEY = 'offer_inbox_entrance_triggered';

const hasSeenOfferPopup = (key) => {
    try {
        return Boolean(sessionStorage.getItem(key));
    } catch (ex) {
        return false;
    }
};

const markOfferPopupSeen = (key) => {
    try {
        sessionStorage.setItem(key, 'true');
    } catch (ex) {
        // nothing to persist against, the popup simply becomes eligible again later
    }
};

// a targeted experienceOfferInbox request returns the single matching campaign
const getTargetedOffer = (data) => data?.experienceOfferInbox?.availableOffers?.[0];

// tolerates both singular/plural CMS field naming, same as HeaderBannersList's exclusions shape
const buildExclusions = (exclusions) => ({
    byPageType: exclusions?.bulk_exceptions || [],
    bySpecificPath: exclusions?.granular_exceptions || [],
    bySpecificPathInclusion: exclusions?.granular_inclusions || exclusions?.granular_inclusion || [],
});

export default function UserExitIntentTracker() {
    const [exitEvent, setExitEvent] = useState(false);
    const [entranceEvent, setEntranceEvent] = useState(false);
    const location = useLocation();
    const dispatch = useDispatch();

    const offerInbox = useSelector(getOfferInboxData);
    const brand = useSelector(getBrandName) || '1800flowers';
    const pageType = useSelector(getPageType);
    const showDefaultOffer = offerInbox?.showDefaultOffers;
    const isPageNotExcluded = useBannerExclusions();

    // the user is already inside the offer inbox experience - drawer opened from the header link,
    // the site wide banner or the view all offers path - do not interrupt that session
    const openDrawers = offerInbox?.isDrawerOpen || {};
    const isOfferInboxOpen = showDefaultOffer
        || Object.keys(openDrawers).some((id) => id !== INSTANCE_ID && openDrawers[id]);

    const {
        data: entranceData,
        loading: entranceLoading,
        error: entranceError,
    } = useExperienceInboxQuery({
        interactionName: `${brand}-GetCampaign-OfferInboxEntrance`,
        targeting: {
            pageType,
        },
        queryName: 'Personalized_offer_entrance',
        apolloOptions: {
            skip: !entranceEvent || exitEvent || isOfferInboxOpen,
        },
    });

    const entranceOffer = getTargetedOffer(entranceData);
    const hasEntranceOffer = Boolean(entranceOffer?.content);

    const isEntranceOpen = entranceEvent && !entranceLoading && !isOfferInboxOpen;
    const isEntranceOfferPageEligible = isPageNotExcluded(buildExclusions(entranceOffer?.content?.exclusions));
    const isEntranceOfferDisplayed = isEntranceOpen && !exitEvent && isEntranceOfferPageEligible
        && Boolean(entranceOffer?.content || entranceOffer?.value?.value?.offerGatewayRecipe?.show_offer);

    // AppShell closes any open popup on navigation, so drop the triggers that opened it and let
    // both flows re-arm from scratch against the new page
    useEffect(() => {
        setEntranceEvent(false);
        setExitEvent(false);
    }, [location?.pathname]);

    useEffect(() => {
        if (exitEvent || hasSeenOfferPopup(ENTRANCE_SESSION_KEY)) {
            return undefined;
        }

        const timer = setTimeout(() => {
            setEntranceEvent(true);
        }, 5000);

        return () => clearTimeout(timer);
    }, [exitEvent, location?.pathname, isOfferInboxOpen]);

    useEffect(() => {
        if (entranceError) {
            mbpLogger.logError({
                function: 'UserExitIntentTracker',
                message: 'Error loading API data from OfferInbox Entrance',
                appName: process.env.npm_package_name,
                module: 'Personalized Offer Inbox',
                jsError: entranceError,
            });
        }
    }, [entranceError]);

    useEffect(() => {
        if (!isEntranceOfferDisplayed || hasSeenOfferPopup(ENTRANCE_SESSION_KEY)) {
            return;
        }

        markOfferPopupSeen(ENTRANCE_SESSION_KEY);
        dispatch(setOfferInboxDrawerOpen({ [INSTANCE_ID]: true }));
        dispatch(emitCustomTrackingEvent({
            action: 'Impression',
            eventSubType: 'feature-tracking',
            category: 'Offer Inbox',
            label: 'entrance_offer_triggered',
            page: { type: pageType },
        }));
    }, [dispatch, pageType, isEntranceOfferDisplayed]);

    const handleExitIntent = useCallback(() => {
        // don't swap out an entrance popup the user is currently looking at
        if (isEntranceOfferDisplayed || hasSeenOfferPopup(EXIT_SESSION_KEY)) return;

        setExitEvent(true);
    }, [isEntranceOfferDisplayed]);

    useExitIntent(handleExitIntent);
    useScrollDetection(handleExitIntent, 1);

    const { data, loading, error } = useExperienceInboxQuery({
        interactionName: `${brand}-GetCampaign-OfferInboxExit`,
        targeting: {
            pageType,
        },
        queryName: 'Personalized_offer_exit',
        apolloOptions: {
            skip: !exitEvent,
        },
    });
    const exitOffer = getTargetedOffer(data);
    const isExitOfferPageEligible = isPageNotExcluded(buildExclusions(exitOffer?.content?.exclusions));

    useEffect(() => {
        const isExitOfferDisplayed = exitEvent && !loading && isExitOfferPageEligible
            && Boolean(exitOffer?.content || exitOffer?.value?.value?.offerGatewayRecipe?.show_offer);

        if (!isExitOfferDisplayed || hasSeenOfferPopup(EXIT_SESSION_KEY)) {
            return;
        }

        dispatch(setOfferInboxData({
            usePopup: true,
        }));

        dispatch(setOfferInboxDrawerOpen({ [INSTANCE_ID]: true }));
        markOfferPopupSeen(EXIT_SESSION_KEY);

        dispatch(emitCustomTrackingEvent({
            action: 'Impression',
            eventSubType: 'feature-tracking',
            category: 'Offer Inbox',
            label: 'exit_intent_offer_triggered',
            page: { type: pageType },
        }));
    }, [pageType, exitOffer, loading, exitEvent, isExitOfferPageEligible]);

    useEffect(() => {
        if (error) {
            mbpLogger.logError({
                function: 'UserExitIntentTracker',
                message: 'Error loading API data from OfferInbox Exit',
                appName: process.env.npm_package_name,
                module: 'Personalized Offer Inbox',
                jsError: error,
            });
        }
    }, [error]);

    useEffect(() => {
        if (showDefaultOffer) {
            dispatch(setOfferInboxDrawerOpen({ [INSTANCE_ID]: true }));
        }
    }, [showDefaultOffer]);

    if (showDefaultOffer) {
        return (
            <OfferInboxContainer
                instanceId={INSTANCE_ID}
                handleClose={() => {
                    dispatch(setOfferInboxData({
                        showDefaultOffers: false,
                    }));
                }}
            />
        );
    }

    if (isEntranceOpen && !exitEvent && isEntranceOfferPageEligible) {
        if (hasEntranceOffer) {
            return (
                <OfferInboxPopup
                    instanceId={INSTANCE_ID}
                    offerData={entranceOffer}
                    impressionName={OFFER_INBOX_ENTRANCE_ITEM}
                    handleClose={() => setEntranceEvent(false)}
                />
            );
        }

        if (entranceOffer?.value?.value?.offerGatewayRecipe?.show_offer) {
            return (
                <OfferInboxContainer
                    instanceId={INSTANCE_ID}
                    handleClose={() => setEntranceEvent(false)}
                />
            );
        }
    }

    if (exitEvent && !loading && isExitOfferPageEligible) {
        if (exitOffer?.content) {
            return (
                <OfferInboxPopup
                    instanceId={INSTANCE_ID}
                    offerData={exitOffer}
                    impressionName={OFFER_INBOX_EXIT_INTENT_ITEM}
                    handleClose={() => setExitEvent(false)}
                />
            );
        }

        if (exitOffer?.value?.value?.offerGatewayRecipe?.show_offer) {
            return (
                <OfferInboxContainer
                    instanceId={INSTANCE_ID}
                    handleClose={() => setExitEvent(false)}
                />
            );
        }
    }

    return <></>;
}
